import React, { useRef, useEffect, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { PageAssignment } from '../../hooks/useTwoPhaseRender';
import { SectionData } from '../../hooks/usePdfExportStateV2';
import { PageRenderer } from './PageRenderer';
import { LogoPlacement } from '../../utils/logoPlacement';

interface PagesContainerProps {
  pageAssignments: PageAssignment[];
  sections: SectionData[];
  data: any; // UniversalJsonResponse
  contentWidth: number;
  contentHeight: number;
  marginPixels: number;
  pageDimensions: { width: number; height: number };
  zoom: number;
  logoDataUrl?: string | null;
  logoPosition?: 'left' | 'center' | 'right';
  logoPlacement?: LogoPlacement;
  companyName?: string;
  headerText?: string;
  footerText?: string;
  reportTitle?: string;
  heights: Map<string, number>;
  onCurrentPageChange?: (pageNumber: number) => void;
  selectedSectionId?: string | null;
  onSelectSection?: (sectionId: string | null) => void;
  onNudgeSection?: (sectionId: string, direction: 'up' | 'down') => void;
  sectionOffsets?: Map<string, number>;
  isEditMode?: boolean;
  editedContent?: Map<string, string>;
  onContentChange?: (contentPath: string, value: string) => void;
  removedBlocks?: Set<string>;
  onDiscoverBlocks?: (sectionId: string, blocks: import('../../hooks/usePdfExportStateV2').DiscoveredBlock[]) => void;
}

export interface PagesContainerRef {
  scrollToPage: (pageNum: number) => void;
}

export const PagesContainer = forwardRef<PagesContainerRef, PagesContainerProps>(({
  pageAssignments,
  sections,
  data,
  contentWidth,
  contentHeight,
  marginPixels,
  pageDimensions,
  zoom,
  logoDataUrl,
  logoPosition,
  logoPlacement,
  companyName,
  headerText,
  footerText,
  reportTitle,
  heights,
  onCurrentPageChange,
  selectedSectionId,
  onSelectSection,
  onNudgeSection,
  sectionOffsets,
  isEditMode,
  editedContent,
  onContentChange,
  removedBlocks,
  onDiscoverBlocks
}, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = pageAssignments.length || 1;

  // Scroll to specific page
  const scrollToPage = useCallback((pageNum: number) => {
    const pageElement = containerRef.current?.querySelector(`[data-page="${pageNum}"]`);
    if (pageElement) {
      pageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  // Expose scrollToPage to parent via ref
  useImperativeHandle(ref, () => ({
    scrollToPage
  }), [scrollToPage]);

  // Track current page on scroll
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let scrollTimeout: NodeJS.Timeout;

    const handleScroll = () => {
      clearTimeout(scrollTimeout);

      scrollTimeout = setTimeout(() => {
        const pageElements = container.querySelectorAll('[data-page]');
        let mostVisiblePage = 1;
        let maxVisibleHeight = 0;

        pageElements.forEach((el) => {
          const rect = el.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();

          const visibleTop = Math.max(rect.top, containerRect.top);
          const visibleBottom = Math.min(rect.bottom, containerRect.bottom);
          const visibleHeight = Math.max(0, visibleBottom - visibleTop);

          if (visibleHeight > maxVisibleHeight) {
            maxVisibleHeight = visibleHeight;
            mostVisiblePage = parseInt(el.getAttribute('data-page') || '1');
          }
        });

        setCurrentPage(mostVisiblePage);
        onCurrentPageChange?.(mostVisiblePage);
      }, 100);
    };

    container.addEventListener('scroll', handleScroll);
    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [onCurrentPageChange]);

  return (
    <div
      ref={containerRef}
      className="pages-container flex-1 overflow-auto bg-gray-400 p-8"
    >
      <div
        className="pages-wrapper flex flex-col items-center gap-8"
        style={{
          transform: `scale(${zoom})`,
          transformOrigin: 'top center',
          minHeight: 'min-content',
          paddingBottom: 32
        }}
      >
        {/* Render all pages */}
        {pageAssignments.length > 0 ? (
          pageAssignments.map(page => (
            <PageRenderer
              key={page.pageNumber}
              page={page}
              sections={sections}
              data={data}
              contentWidth={contentWidth}
              contentHeight={contentHeight}
              marginPixels={marginPixels}
              pageDimensions={pageDimensions}
              totalPages={totalPages}
              logoDataUrl={logoDataUrl}
              logoPosition={logoPosition}
              logoPlacement={logoPlacement}
              companyName={companyName}
              headerText={headerText}
              footerText={footerText}
              reportTitle={reportTitle}
              heights={heights}
              selectedSectionId={selectedSectionId}
              onSelectSection={onSelectSection}
              onNudgeSection={onNudgeSection}
              sectionOffsets={sectionOffsets}
              isEditMode={isEditMode}
              editedContent={editedContent}
              onContentChange={onContentChange}
              removedBlocks={removedBlocks}
              onDiscoverBlocks={onDiscoverBlocks}
            />
          ))
        ) : (
          // Empty state
          <div
            className="pdf-page bg-white shadow-2xl relative flex items-center justify-center"
            style={{
              width: pageDimensions.width,
              height: pageDimensions.height
            }}
          >
            <div className="text-gray-400 text-center">
              <p className="text-lg font-medium">No content to display</p>
              <p className="text-sm mt-2">Enable sections in the sidebar to preview them</p>
            </div>
          </div>
        )}

        {/* Info panel */}
        <div className="text-center text-sm text-gray-600 bg-gray-200 px-4 py-2 rounded-lg shadow mt-4">
          <p className="font-medium">{totalPages} page{totalPages !== 1 ? 's' : ''} • {Math.round(marginPixels / 3.78)}mm margins</p>
          <p className="text-xs text-gray-500 mt-1">
            Content: {contentWidth}×{contentHeight}px •
            Page: {pageDimensions.width}×{pageDimensions.height}px
          </p>
        </div>
      </div>

      {/* Scrollbar and print styles */}
      <style>{`
        .pages-container {
          scrollbar-width: thin;
          scrollbar-color: #94a3b8 #cbd5e1;
        }

        .pages-container::-webkit-scrollbar {
          width: 10px;
        }

        .pages-container::-webkit-scrollbar-track {
          background: #cbd5e1;
        }

        .pages-container::-webkit-scrollbar-thumb {
          background: #94a3b8;
          border-radius: 5px;
        }

        .pages-container::-webkit-scrollbar-thumb:hover {
          background: #64748b;
        }

        .pdf-page {
          transition: box-shadow 0.2s;
          flex-shrink: 0;
        }

        .pdf-page:hover {
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.35),
                      0 0 0 1px rgba(0, 0, 0, 0.15) !important;
        }

        /* Print styles */
        @media print {
          .pages-container {
            background: white !important;
            padding: 0 !important;
            overflow: visible !important;
            height: auto !important;
          }

          .pages-wrapper {
            transform: none !important;
            padding: 0 !important;
            min-height: auto !important;
            gap: 0 !important;
          }

          .pdf-page {
            box-shadow: none !important;
            margin: 0 auto !important;
            page-break-after: always !important;
            break-after: page !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }

          .pdf-page:last-child {
            page-break-after: auto !important;
            break-after: auto !important;
          }

          /* Hide the info panel at the bottom */
          .pages-wrapper > div.text-center {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
});

PagesContainer.displayName = 'PagesContainer';

export default PagesContainer;
